import { useMemo, useRef, useState } from "react";

// Building blocks for the AI screening dashboards. Pure display components:
// they receive the numbers from GET /screening/stats and draw them.
// Used by ScreeningList (recruiter / team lead) and AdminScreeningDashboard.

// ---------------------------------------------------------------------------
// Date helpers (calendar days in the viewer's local time, as "YYYY-MM-DD")
// ---------------------------------------------------------------------------
const pad = (n) => String(n).padStart(2, "0");
export const toDateKey = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
export const daysAgoKey = (days) => {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return toDateKey(d);
};
const shortDay = (key) =>
  new Date(`${key}T00:00:00`).toLocaleDateString(undefined, { day: "numeric", month: "short" });

export const RANGE_PRESETS = [
  { id: "7d", label: "7 days", days: 7 },
  { id: "30d", label: "30 days", days: 30 },
  { id: "90d", label: "90 days", days: 90 },
];

// Range for a preset: today and the (days - 1) days before it.
export const presetRange = (days) => ({ startDate: daysAgoKey(days - 1), endDate: toDateKey(new Date()) });

// ---------------------------------------------------------------------------
// Range picker: preset chips + custom From / To
// ---------------------------------------------------------------------------
export function RangePicker({ value, onChange, disabled }) {
  const [custom, setCustom] = useState({ startDate: value.startDate, endDate: value.endDate });
  const activePreset = RANGE_PRESETS.find((p) => {
    const r = presetRange(p.days);
    return r.startDate === value.startDate && r.endDate === value.endDate;
  });

  return (
    <div className="st-range" role="group" aria-label="Date range">
      <div className="st-range-chips">
        {RANGE_PRESETS.map((p) => (
          <button
            key={p.id}
            type="button"
            className={`st-chip${activePreset?.id === p.id ? " is-active" : ""}`}
            onClick={() => {
              const r = presetRange(p.days);
              setCustom(r);
              onChange(r);
            }}
            disabled={disabled}
            aria-pressed={activePreset?.id === p.id}
          >
            {p.label}
          </button>
        ))}
      </div>
      <div className="st-range-custom">
        <input
          type="date"
          aria-label="From"
          value={custom.startDate}
          max={custom.endDate || undefined}
          onChange={(e) => setCustom((c) => ({ ...c, startDate: e.target.value }))}
          disabled={disabled}
        />
        <span aria-hidden="true">→</span>
        <input
          type="date"
          aria-label="To"
          value={custom.endDate}
          min={custom.startDate || undefined}
          onChange={(e) => setCustom((c) => ({ ...c, endDate: e.target.value }))}
          disabled={disabled}
        />
        <button
          type="button"
          className="st-chip st-chip-apply"
          onClick={() => custom.startDate && custom.endDate && onChange(custom)}
          disabled={disabled || !custom.startDate || !custom.endDate}
        >
          Apply
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// KPI tiles
// ---------------------------------------------------------------------------
const fmtHours = (h) => {
  if (h == null) return "—";
  if (h < 1) return `${Math.max(1, Math.round(h * 60))} min`;
  if (h < 48) return `${Math.round(h)} h`;
  return `${Math.round(h / 24)} days`;
};

function Kpi({ label, value, sub, tone }) {
  return (
    <div className={`st-kpi${tone ? ` st-kpi-${tone}` : ""}`}>
      <span className="st-kpi-label">{label}</span>
      <span className="st-kpi-value">{value}</span>
      {sub && <span className="st-kpi-sub">{sub}</span>}
    </div>
  );
}

// verdicts = { ADVANCE, HOLD, NOT_RECOMMENDED } counts from GET /screening/stats
export function KpiGrid({ kpis, verdicts, loading }) {
  const k = kpis || {};
  const vd = verdicts || {};
  const v = (n) => (loading && !kpis ? "…" : n ?? "—");
  return (
    <div className="st-kpis">
      <Kpi
        label="Screenings scheduled"
        value={v(k.total)}
        sub={kpis ? `${k.sent} links sent · ${k.drafts} drafts` : null}
      />
      <Kpi
        label="Awaiting candidate"
        value={v(kpis ? k.awaiting + k.inProgress : null)}
        sub={kpis ? `${k.notOpened} not opened · ${k.inProgress} in progress` : null}
        tone={k.notOpened > 0 ? "attention" : null}
      />
      <Kpi
        label="Completed"
        value={v(k.completed)}
        sub={kpis ? (k.completionRate != null ? `${k.completionRate}% of links sent` : "No links sent yet") : null}
      />
      {/* Readiness instead of an average score: an average of 95, 90, 30 and 25
          says "60, everyone is middling" and hides who is ready and who needs work. */}
      <Kpi
        label="Ready for client"
        value={v(kpis ? vd.ADVANCE || 0 : null)}
        sub={
          kpis
            ? k.reportsReady
              ? `of ${k.reportsReady} reports · ${vd.HOLD || 0} hold · ${vd.NOT_RECOMMENDED || 0} not recommended`
              : "No reports yet"
            : null
        }
        tone="good"
      />
      <Kpi
        label="Avg. time to finish"
        value={v(kpis ? fmtHours(k.avgTurnaroundHours) : null)}
        sub={kpis ? `${k.expired} expired · ${k.cancelled} withdrawn` : null}
        tone={k.expired > 0 ? "muted" : null}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Trend chart: screenings scheduled vs completed per day (SVG, no library)
// ---------------------------------------------------------------------------
const W = 720;
const H = 260;
const M = { top: 12, right: 16, bottom: 28, left: 34 };

function niceMax(n) {
  if (n <= 4) return 4;
  const step = Math.pow(10, Math.floor(Math.log10(n)));
  const scaled = n / step;
  const nice = scaled <= 2 ? 2 : scaled <= 5 ? 5 : 10;
  return Math.ceil(n / ((nice * step) / 4)) * ((nice * step) / 4);
}

export function TrendChart({ trend = [] }) {
  const wrapRef = useRef(null);
  const [hover, setHover] = useState(null); // index of hovered day

  const { xs, yScale, maxY, scheduledPath, completedPath, ticks, totals } = useMemo(() => {
    const n = trend.length;
    const innerW = W - M.left - M.right;
    const innerH = H - M.top - M.bottom;
    const max = niceMax(Math.max(1, ...trend.map((d) => Math.max(d.scheduled, d.completed))));
    const x = (i) => M.left + (n <= 1 ? innerW / 2 : (i / (n - 1)) * innerW);
    const y = (v) => M.top + innerH - (v / max) * innerH;
    const path = (key) => trend.map((d, i) => `${i === 0 ? "M" : "L"}${x(i).toFixed(1)},${y(d[key]).toFixed(1)}`).join(" ");
    // ~6 date labels along the bottom, always including the last day.
    const every = Math.max(1, Math.ceil(n / 6));
    const tickIdx = trend.map((_, i) => i).filter((i) => i % every === 0 || i === n - 1);
    if (tickIdx.length > 1 && n - 1 - tickIdx[tickIdx.length - 2] < every / 2) tickIdx.splice(tickIdx.length - 2, 1);
    return {
      xs: trend.map((_, i) => x(i)),
      yScale: y,
      maxY: max,
      scheduledPath: path("scheduled"),
      completedPath: path("completed"),
      ticks: tickIdx,
      totals: trend.reduce(
        (t, d) => ({ scheduled: t.scheduled + d.scheduled, completed: t.completed + d.completed }),
        { scheduled: 0, completed: 0 }
      ),
    };
  }, [trend]);

  if (!trend.length) return <div className="st-chart-empty">No data for this range.</div>;

  const onMove = (e) => {
    const svg = wrapRef.current?.querySelector("svg");
    if (!svg) return;
    const rect = svg.getBoundingClientRect();
    const px = ((e.clientX - rect.left) / rect.width) * W;
    let best = 0;
    xs.forEach((x, i) => {
      if (Math.abs(x - px) < Math.abs(xs[best] - px)) best = i;
    });
    setHover(best);
  };

  const yTicks = [0, maxY / 4, maxY / 2, (3 * maxY) / 4, maxY];
  const h = hover != null ? trend[hover] : null;
  const tipLeftPct = hover != null ? (xs[hover] / W) * 100 : 0;

  return (
    <div className="st-trend" ref={wrapRef}>
      <div className="st-legend">
        <span className="st-legend-item">
          <span className="st-swatch st-swatch-scheduled" aria-hidden="true" />
          Scheduled <b>{totals.scheduled}</b>
        </span>
        <span className="st-legend-item">
          <span className="st-swatch st-swatch-completed" aria-hidden="true" />
          Completed by candidate <b>{totals.completed}</b>
        </span>
      </div>

      <div className="st-trend-plot" onMouseMove={onMove} onMouseLeave={() => setHover(null)}>
        <svg
          viewBox={`0 0 ${W} ${H}`}
          role="img"
          aria-label={`Screenings per day: ${totals.scheduled} scheduled and ${totals.completed} completed in this range.`}
        >
          {yTicks.map((t) => (
            <g key={t}>
              <line x1={M.left} x2={W - M.right} y1={yScale(t)} y2={yScale(t)} className="st-grid" />
              <text x={M.left - 8} y={yScale(t) + 4} className="st-axis" textAnchor="end">
                {Number.isInteger(t) ? t : ""}
              </text>
            </g>
          ))}
          {ticks.map((i) => (
            <text key={i} x={xs[i]} y={H - 8} className="st-axis" textAnchor="middle">
              {shortDay(trend[i].date)}
            </text>
          ))}

          {hover != null && <line x1={xs[hover]} x2={xs[hover]} y1={M.top} y2={H - M.bottom} className="st-crosshair" />}

          <path d={scheduledPath} className="st-line st-line-scheduled" />
          <path d={completedPath} className="st-line st-line-completed" />

          {hover != null && (
            <>
              <circle cx={xs[hover]} cy={yScale(h.scheduled)} r="4.5" className="st-dot st-dot-scheduled" />
              <circle cx={xs[hover]} cy={yScale(h.completed)} r="4.5" className="st-dot st-dot-completed" />
            </>
          )}
        </svg>

        {h && (
          <div
            className={`st-tooltip${tipLeftPct > 70 ? " is-left" : ""}`}
            style={{ left: `${tipLeftPct}%` }}
            role="status"
          >
            <div className="st-tooltip-date">
              {new Date(`${h.date}T00:00:00`).toLocaleDateString(undefined, { weekday: "short", day: "numeric", month: "short" })}
            </div>
            <div className="st-tooltip-row">
              <span className="st-swatch st-swatch-scheduled" aria-hidden="true" />
              Scheduled <b>{h.scheduled}</b>
            </div>
            <div className="st-tooltip-row">
              <span className="st-swatch st-swatch-completed" aria-hidden="true" />
              Completed <b>{h.completed}</b>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Outcomes: AI recommendation split + score distribution
// ---------------------------------------------------------------------------
const VERDICT_ROWS = [
  { key: "ADVANCE", label: "Advance", icon: "✓" },
  { key: "HOLD", label: "Hold", icon: "!" },
  { key: "NOT_RECOMMENDED", label: "Not recommended", icon: "✕" },
];

export function OutcomeBreakdown({ verdicts = {}, scoreBands = [] }) {
  const total = VERDICT_ROWS.reduce((s, r) => s + (verdicts[r.key] || 0), 0);
  const maxBand = Math.max(1, ...scoreBands.map((b) => b.count));

  return (
    <div className="st-outcomes">
      <div>
        <h4 className="st-subhead">AI recommendation</h4>
        {total === 0 ? (
          <p className="st-muted">No finished reports in this range yet.</p>
        ) : (
          <>
            <div className="st-stack" role="img" aria-label="Share of reports by AI recommendation">
              {VERDICT_ROWS.map((r) =>
                verdicts[r.key] ? (
                  <span
                    key={r.key}
                    className={`st-stack-seg st-v-${r.key}`}
                    style={{ flexGrow: verdicts[r.key] }}
                    title={`${r.label}: ${verdicts[r.key]}`}
                  />
                ) : null
              )}
            </div>
            <ul className="st-verdict-list">
              {VERDICT_ROWS.map((r) => (
                <li key={r.key}>
                  <span className={`st-verdict-icon st-v-${r.key}`} aria-hidden="true">
                    {r.icon}
                  </span>
                  <span className="st-verdict-name">{r.label}</span>
                  <b>{verdicts[r.key] || 0}</b>
                  <span className="st-muted">{Math.round(((verdicts[r.key] || 0) / total) * 100)}%</span>
                </li>
              ))}
            </ul>
          </>
        )}
      </div>

      <div>
        <h4 className="st-subhead">Score distribution</h4>
        {scoreBands.every((b) => b.count === 0) ? (
          <p className="st-muted">Scores appear once reports are ready.</p>
        ) : (
          <div className="st-bands">
            {scoreBands.map((b, i) => (
              <div key={b.label} className="st-band" title={`Score ${b.label}: ${b.count}`}>
                <span className="st-band-count">{b.count}</span>
                <div className="st-band-track">
                  <div
                    className={`st-band-bar st-band-${i}`}
                    style={{ height: `${b.count ? Math.max(6, (b.count / maxBand) * 100) : 0}%` }}
                  />
                </div>
                <span className="st-band-label">{b.label}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}